import { useFlowTheme } from '@api/flows';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import { themeToCssVars } from '@descope/page-editor-components';
import '@descope/page-editor-components/dist/style.css';
import { css, Global } from '@emotion/react';
import { Box, Grid } from '@mui/material';
import { useFontsLoader } from 'hooks';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { IScreenMetadata } from 'types/flows';
import Canvas from './Canvas';
import { generateInteractions } from './helpers';
import LeftBar from './LeftBar';
import SettingsPanel from './SettingsPanel';
import { CanvasContext } from './ActionsBar/CanvasContext';

const PageEditor: React.FC<{
	htmlTemplate: IScreenMetadata['htmlTemplate'];
	onDirty?: (isDirty: boolean) => void;
	updatePageCallback?: (func: () => IScreenMetadata) => void;
}> = ({ htmlTemplate, updatePageCallback, onDirty }) => {
	const { actions, enabled, serialize, isRootExists, isNodeSelected, isDirty } =
		useEditor((state, query) => ({
			enabled: state.options.enabled,
			isRootExists: !!state.nodes[ROOT_NODE],
			isNodeSelected: !!state.events.selected.size,
			isDirty: query.history.canUndo(),
			serialize: query.serialize
		}));

	const { data } = useFlowTheme();

	const { theme, width, height } = useContext(CanvasContext);

	const themeOverrides = useMemo(
		() => ({
			[`[data-theme="${theme}"]`]: themeToCssVars(data?.cssTemplate?.[theme!]!)
		}),
		[data?.cssTemplate, theme]
	);

	useFontsLoader(data?.cssTemplate?.light?.typography?.fontFamilies);

	const handleSave = useCallback((): IScreenMetadata => {
		const newHTMLTemplate = JSON.parse(serialize());
		const interactions = generateInteractions(newHTMLTemplate);

		return {
			htmlTemplate: newHTMLTemplate,
			interactions
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		updatePageCallback?.(handleSave);
	}, [updatePageCallback, handleSave]);

	useEffect(() => {
		actions.history.ignore().deserialize(JSON.stringify(htmlTemplate));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [htmlTemplate]);

	useEffect(() => {
		if (enabled && isRootExists && !isNodeSelected) {
			actions.selectNode(ROOT_NODE);
		}
	}, [actions, enabled, isNodeSelected, isRootExists]);

	useEffect(() => {
		onDirty?.(isDirty);
	}, [isDirty, onDirty]);

	return (
		<>
			{/* we need to update the global style with the user's style css vars
			 so when dragging elements, they will keep their custom style and not use the default theme style */}
			<Global styles={css(themeOverrides)} />
			<Box
				flexGrow={1}
				overflow="hidden"
				display="flex"
				flexDirection="column"
				data-testid="screen-editor"
				className="screen-editor-container"
			>
				<Grid container sx={{ overflow: 'hidden', height: '100%' }}>
					<Grid item overflow="auto" height="100%">
						<LeftBar />
					</Grid>
					<Grid
						item
						xs
						overflow="auto"
						height="100%"
						className="craftjs-renderer"
						data-theme={theme}
					>
						<Canvas width={width!} height={height!} />
					</Grid>
					<Grid item width={240} overflow="auto" height="100%">
						<SettingsPanel />
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

PageEditor.defaultProps = {
	updatePageCallback: undefined,
	onDirty: undefined
};

export default PageEditor;
